import { Link } from "react-router-dom";
import { Section, Overline, SectionHeading } from "../components/Section";
import { TRUST_METRICS, PROCESS_STEPS, SERVICES, SPECIALTIES, MEMBERSHIP } from "../data/site";
import ConsultationForm from "../components/ConsultationForm";
import SecondOpinionBlock from "../components/SecondOpinionBlock";
import WhatsAppButton from "../components/WhatsAppButton";
import ConciergePlans from "../components/ConciergePlans";
import { WA_DISPLAY } from "../lib/whatsapp";
import { ArrowUpRight, ShieldCheck, Clock, Globe2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg0Mjk3ODI3fDA&ixlib=rb-4.1.0&q=85";
const CONCIERGE_IMG = "https://images.unsplash.com/photo-1660557989695-14fac79c086d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjb25jaWVyZ2UlMjBkZXNrfGVufDB8fHx8MTc4NDI5NzgyN3ww&ixlib=rb-4.1.0&q=85";
const COUPLE_IMG = "https://images.unsplash.com/photo-1746192703468-dedf3fc11d87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzZW5pb3IlMjBjb3VwbGUlMjBzbWlsaW5nfGVufDB8fHx8MTc4NDI5NzgyN3ww&ixlib=rb-4.1.0&q=85";
const GOLD_IMG = "https://images.unsplash.com/photo-1747656336410-b8a5b4e9bc2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjb25jaWVyZ2UlMjBkZXNrfGVufDB8fHx8MTc4NDI5NzgyN3ww&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-end pb-16 pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/85 via-[#0B1F3A]/70 to-[#0B1F3A]/95" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 text-white">
            <div className="rise overline text-[#C8A96A]">Private Medical Concierge</div>
            <h1 className="rise rise-2 mt-6 font-serif font-light text-white text-[44px] sm:text-[64px] lg:text-[84px] leading-[0.98] tracking-tight max-w-4xl">
              Priority access to India's<br className="hidden md:block"/> finest medicine — <em className="text-[#C8A96A] not-italic font-normal">personally managed.</em>
            </h1>
            <p className="rise rise-3 mt-8 max-w-2xl text-white/75 text-[16px] leading-relaxed">
              For NRIs and international patients. One dedicated concierge coordinates every
              non-clinical detail of your journey — from second opinions and specialist
              appointments to travel, accommodation and recovery.
            </p>
            <div className="rise rise-4 mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[#C8A96A] text-[#0B1F3A] text-[12px] tracking-[0.18em] uppercase hover:bg-white transition-colors duration-300"
              >
                Request Confidential Consultation
              </Link>
              <WhatsAppButton
                variant="inverted"
                testId="hero-cta-secondary"
                message="Hello Easyilaaz Concierge — I would like to speak with a concierge about medical care in India."
              >
                Speak on WhatsApp
              </WhatsAppButton>
            </div>
          </div>
          <div className="lg:col-span-4 rise rise-5 hidden lg:block">
            <div className="bg-white/8 backdrop-blur-md border border-white/15 p-8">
              <div className="overline text-[#C8A96A]">Assurances</div>
              <ul className="mt-6 space-y-5 text-white/85 text-[13px] leading-relaxed">
                <li className="flex gap-3"><ShieldCheck size={18} strokeWidth={1.25} className="text-[#C8A96A] shrink-0 mt-0.5"/> Independent — we represent you, not hospitals.</li>
                <li className="flex gap-3"><Clock size={18} strokeWidth={1.25} className="text-[#C8A96A] shrink-0 mt-0.5"/> Case manager assigned within 24 hours.</li>
                <li className="flex gap-3"><Globe2 size={18} strokeWidth={1.25} className="text-[#C8A96A] shrink-0 mt-0.5"/> Serving guests across 22 countries.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section data-testid="trust-section" className="bg-white border-b border-[#E6E9EF]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
          {TRUST_METRICS.map((m) => (
            <div key={m.label} className="text-center md:text-left" data-testid={`metric-${m.label.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="font-serif text-4xl lg:text-5xl text-[#0B1F3A]">{m.value}</div>
              <div className="mt-2 overline text-[#0B1F3A]/50">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROMISE / ABOUT SNAPSHOT */}
      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <img src={CONCIERGE_IMG} alt="Concierge desk" className="w-full h-[520px] object-cover" />
          </div>
          <div className="lg:col-span-6">
            <Overline>Independent · Discreet · Personal</Overline>
            <h2 className="mt-5 font-serif text-4xl lg:text-6xl leading-[1.05] text-[#0B1F3A]">
              We represent<br/>the patient — <em className="text-[#C8A96A] not-italic">only</em>.
            </h2>
            <p className="mt-8 text-[15px] leading-[1.8] text-[#0B1F3A]/75 max-w-xl">
              Easyilaaz is not a hospital, and not a medical tourism agency. We are your
              independent healthcare concierge — quietly navigating India's most respected
              hospitals and specialists on your behalf, so that you may focus on healing,
              and your family may focus on you.
            </p>
            <div className="gold-rule mt-10 max-w-md" />
            <p className="mt-6 font-serif italic text-[22px] text-[#0B1F3A]/80 max-w-md">
              "One trusted point of contact. From the first question to the final follow-up."
            </p>
          </div>
        </div>
      </Section>

      {/* PROCESS */}
      <Section className="bg-[#F5F7FA]">
        <SectionHeading eyebrow="How Easyilaaz Works" title="Five quiet steps. Considered from beginning to end." />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {PROCESS_STEPS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`process-step-${i+1}`}
              className={`relative bg-white p-8 border border-transparent hover:border-[#C8A96A]/40 transition-colors duration-500 ${i === 0 ? "lg:col-span-2" : ""}`}
            >
              <div className="font-serif text-4xl text-[#C8A96A]">{s.n}</div>
              <div className="mt-6 font-serif text-2xl text-[#0B1F3A] leading-tight">{s.title}</div>
              <div className="mt-3 text-[13px] leading-relaxed text-[#0B1F3A]/70">{s.body}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* SERVICES */}
      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Overline>The Concierge Portfolio</Overline>
            <h2 className="mt-5 font-serif text-4xl lg:text-6xl leading-[1.05] text-[#0B1F3A]">
              Every detail,<br/>quietly attended to.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#0B1F3A]/70 max-w-md">
              Eight core services. One concierge. Available as bespoke arrangements
              or through our Silver, Gold, and Platinum memberships.
            </p>
            <Link to="/services" data-testid="services-explore-link" className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#0B1F3A] link-elegant">
              Explore the portfolio <ArrowUpRight size={16} strokeWidth={1.25}/>
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E6E9EF]">
            {SERVICES.map((s, i) => (
              <div key={s.title} data-testid={`service-tile-${i+1}`} className="bg-white p-8 hover:bg-[#F5F7FA]/60 transition-colors duration-300">
                <div className="overline text-[#C8A96A]">0{i+1}</div>
                <div className="mt-4 font-serif text-2xl text-[#0B1F3A]">{s.title}</div>
                <div className="mt-2 text-[13px] leading-relaxed text-[#0B1F3A]/70">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SPECIALTIES */}
      <Section className="bg-[#0B1F3A] text-white relative overflow-hidden grain">        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 relative">
          <div className="lg:col-span-5">
            <Overline className="text-[#C8A96A]">Launch Specialties</Overline>
            <h2 className="mt-5 font-serif text-4xl lg:text-6xl leading-[1.05] text-white">
              Four disciplines.<br/>Curated with care.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-white/70 max-w-md">
              We launch with the four specialties most sought by our members —
              expanding into oncology, neurology, organ transplant and preventive
              health through 2026.
            </p>
            <Link to="/specialties" data-testid="specialties-explore-link" className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#C8A96A] link-elegant">
              View all specialties <ArrowUpRight size={16} strokeWidth={1.25}/>
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            {SPECIALTIES.map((s, i) => (
              <div key={s.key} data-testid={`specialty-card-${s.key}`} className="border border-white/10 p-8 hover:border-[#C8A96A]/50 transition-colors duration-500">
                <div className="overline text-[#C8A96A]">0{i+1}</div>
                <div className="mt-3 font-serif text-3xl text-white">{s.name}</div>
                <div className="mt-2 text-[13px] tracking-wide text-white/60">{s.tagline}</div>
                <div className="mt-5 text-[13px] leading-relaxed text-white/70">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SECOND OPINION — SIGNATURE OFFER */}
      <SecondOpinionBlock />

      {/* MEMBERSHIP */}
      <Section className="bg-white relative overflow-hidden">
        <img src={GOLD_IMG} alt="" className="absolute -right-40 -top-20 w-[600px] opacity-15 pointer-events-none select-none" />
        <div className="relative">
          <SectionHeading
            eyebrow="Concierge Plans"
            title="Three tiers. One promise of care."
          />
          <ConciergePlans />
        </div>
      </Section>

      {/* SOCIAL/AUDIENCE STRIP */}
      <Section className="bg-[#F5F7FA]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Overline>For families across the world</Overline>
            <h2 className="mt-5 font-serif text-4xl lg:text-6xl leading-[1.05] text-[#0B1F3A]">
              Trusted by NRIs<br/>in 22 countries.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#0B1F3A]/75 max-w-xl">
              From the United States, Canada and the United Kingdom to the Emirates,
              Australia and beyond — Easyilaaz members return to India for care with
              confidence, dignity, and one number they can rely on.
            </p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <img src={COUPLE_IMG} alt="Members" className="w-full h-[520px] object-cover" />
          </div>
        </div>
      </Section>

      {/* CONSULTATION FORM CTA */}
      <Section id="consult" className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Overline>Begin the conversation</Overline>
            <h2 className="mt-5 font-serif text-4xl lg:text-6xl leading-[1.05] text-[#0B1F3A]">
              A private line<br/>to your concierge.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#0B1F3A]/75 max-w-md">
              Share as much or as little as you wish. Within one business day, a
              dedicated case manager will respond — privately, and without obligation.
            </p>
            <div className="gold-rule mt-10 max-w-xs" />
            <div className="mt-6 text-[13px] text-[#0B1F3A]/60 space-y-1">
              <div>concierge@easyilaaz.com</div>
              <div>WhatsApp {WA_DISPLAY} · 24 × 7</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ConsultationForm />
          </div>
        </div>
      </Section>
    </>
  );
}
